import { Injectable } from '@angular/core';
import {
  ParkingRevenueKpiCard,
  ParkingRevenueMarketingDashboardDto,
  ParkingRevenueMarketingResult,
  ParkingRevenueMarketingSummaryDto,
  ParkingRevenueMonth,
  ParkingRevenueMonthlySeriesDto,
  ParkingRevenueMonthlyTypeFeeSeriesDto,
  ParkingRevenueTrendInsight,
  ParkingRevenueTypeFee,
  ParkingRevenueTypeFeeBreakdownDto,
  ParkingRevenueTypeFeeSeries,
  RevenueTrendDirection,
} from '../../models/finance/parking-revenue-marketing.model';

const TYPE_FEE_COLORS = [
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#f43f5e',
  '#0284c7',
  '#14b8a6',
  '#84cc16',
  '#64748b',
];

@Injectable({ providedIn: 'root' })
export class MapperParkingRevenueMarketingService {
  mapDashboard(dto: unknown): ParkingRevenueMarketingResult {
    const raw = (dto ?? {}) as ParkingRevenueMarketingDashboardDto & Record<string, unknown>;
    const summary = this.mapSummary(
      (raw.summary ?? raw['Summary']) as ParkingRevenueMarketingSummaryDto | Record<string, unknown>,
    );
    const typeFeeBreakdown = this.mapTypeFees(
      (raw.typeFeeBreakdown ?? raw['TypeFeeBreakdown'] ?? []) as unknown[],
    );
    const monthlyFlat = (raw.monthlyByTypeFee ?? raw['MonthlyByTypeFee'] ?? []) as unknown[];
    const monthlyRaw = (raw.monthlySeries ?? raw['MonthlySeries'] ?? []) as unknown[];
    const monthlySeries = this.mapMonthly(monthlyRaw, summary, monthlyFlat, typeFeeBreakdown);
    const typeFeeMonthlySeries = this.mapTypeFeeMonthly(monthlyFlat, monthlySeries, typeFeeBreakdown);
    const chartMaxAmount = Math.max(1, ...monthlySeries.map((m) => m.amount));

    if (monthlySeries.some((m) => m.isIncompleteMonth)) {
      const lastCompleted = [...monthlySeries]
        .reverse()
        .find((m) => !m.isIncompleteMonth && m.momChangePercent !== null);
      summary.momChangePercent = lastCompleted?.momChangePercent ?? null;
    }

    return {
      summary,
      monthlySeries,
      typeFeeBreakdown,
      typeFeeMonthlySeries,
      typeFeeDonutGradient: this.buildDonut(typeFeeBreakdown, summary.totalAmount),
      kpiCards: this.buildKpiCards(summary),
      trendInsight: this.buildTrendInsight(monthlySeries),
      chartMaxAmount,
      siteName: String(raw.siteName ?? raw['SiteName'] ?? ''),
      siteAddress: String(raw.siteAddress ?? raw['SiteAddress'] ?? ''),
      siteTaxId: String(raw.siteTaxId ?? raw['SiteTaxId'] ?? ''),
    };
  }

  private mapSummary(
    raw: ParkingRevenueMarketingSummaryDto | Record<string, unknown>,
  ): ParkingRevenueMarketingResult['summary'] {
    const s = (raw ?? {}) as ParkingRevenueMarketingSummaryDto & Record<string, unknown>;
    const mom = s.momChangePercent ?? s['MomChangePercent'];
    return {
      totalAmount: Number(s.totalAmount ?? s['TotalAmount'] ?? 0),
      receiptCount: Number(s.receiptCount ?? s['ReceiptCount'] ?? 0),
      peakMonthAmount: Number(s.peakMonthAmount ?? s['PeakMonthAmount'] ?? 0),
      peakMonthLabel: String(s.peakMonthLabel ?? s['PeakMonthLabel'] ?? ''),
      lowestMonthAmount: Number(s.lowestMonthAmount ?? s['LowestMonthAmount'] ?? 0),
      lowestMonthLabel: String(s.lowestMonthLabel ?? s['LowestMonthLabel'] ?? ''),
      momChangePercent: mom === null || mom === undefined ? null : Number(mom),
      typeFeeCount: Number(s.typeFeeCount ?? s['TypeFeeCount'] ?? 0),
      topTypeFeeName: String(s.topTypeFeeName ?? s['TopTypeFeeName'] ?? ''),
      topTypeFeeAmount: Number(s.topTypeFeeAmount ?? s['TopTypeFeeAmount'] ?? 0),
    };
  }

  private mapTypeFees(items: unknown[]): ParkingRevenueTypeFee[] {
    const mapped = items.map((it, index) => {
      const r = (it ?? {}) as ParkingRevenueTypeFeeBreakdownDto & Record<string, unknown>;
      return {
        typeFeeId: Number(r.typeFeeId ?? r['TypeFeeId'] ?? 0),
        typeFeeName: String(r.typeFeeName ?? r['TypeFeeName'] ?? 'ไม่ระบุประเภท'),
        amount: Number(r.amount ?? r['Amount'] ?? 0),
        receiptCount: Number(r.receiptCount ?? r['ReceiptCount'] ?? 0),
        percent: Number(r.percent ?? r['Percent'] ?? 0),
        barPct: 0,
        color: TYPE_FEE_COLORS[index % TYPE_FEE_COLORS.length],
        donutStartPct: 0,
        donutEndPct: 0,
      };
    });
    const max = Math.max(1, ...mapped.map((x) => x.amount));
    let cursor = 0;
    return mapped.map((x) => {
      const start = cursor;
      const end = cursor + x.percent;
      cursor = end;
      return {
        ...x,
        barPct: Math.round((x.amount / max) * 100),
        donutStartPct: start,
        donutEndPct: end,
      };
    });
  }

  private mapMonthly(
    items: unknown[],
    summary: ParkingRevenueMarketingResult['summary'],
    monthlyFlatRaw: unknown[],
    typeFees: ParkingRevenueTypeFee[],
  ): ParkingRevenueMonth[] {
    const flat = monthlyFlatRaw.map((it) => {
      const r = (it ?? {}) as ParkingRevenueMonthlyTypeFeeSeriesDto & Record<string, unknown>;
      return {
        year: Number(r.year ?? r['Year'] ?? 0),
        month: Number(r.month ?? r['Month'] ?? 0),
        typeFeeId: Number(r.typeFeeId ?? r['TypeFeeId'] ?? 0),
        typeFeeName: String(r.typeFeeName ?? r['TypeFeeName'] ?? 'ไม่ระบุประเภท'),
        amount: Number(r.amount ?? r['Amount'] ?? 0),
        receiptCount: Number(r.receiptCount ?? r['ReceiptCount'] ?? 0),
      };
    });

    const colorByKey = new Map(
      typeFees.map((t) => [`${t.typeFeeId}|${t.typeFeeName}`, t.color] as const),
    );

    const maxAmount = Math.max(
      1,
      ...items.map((it) => {
        const r = (it ?? {}) as ParkingRevenueMonthlySeriesDto & Record<string, unknown>;
        return Number(r.amount ?? r['Amount'] ?? 0);
      }),
    );

    return items.map((it, index) => {
      const r = (it ?? {}) as ParkingRevenueMonthlySeriesDto & Record<string, unknown>;
      const year = Number(r.year ?? r['Year'] ?? 0);
      const month = Number(r.month ?? r['Month'] ?? 0);
      const label = String(r.label ?? r['Label'] ?? `${month}/${year}`);
      const amount = Number(r.amount ?? r['Amount'] ?? 0);
      const receiptCount = Number(r.receiptCount ?? r['ReceiptCount'] ?? 0);
      const monthKey = `${String(month).padStart(2, '0')}/${year}`;

      let prevAmount: number | null = null;
      let momDelta: number | null = null;
      let momChangePercent: number | null = null;
      let trend: RevenueTrendDirection | null = null;
      const isIncompleteMonth = this.isIncompleteCalendarMonth(year, month);

      if (index > 0) {
        const prev = items[index - 1] as ParkingRevenueMonthlySeriesDto & Record<string, unknown>;
        prevAmount = Number(prev.amount ?? prev['Amount'] ?? 0);
        momDelta = amount - prevAmount;
        if (isIncompleteMonth) {
          momChangePercent = null;
          trend = 'pending';
        } else {
          momChangePercent = this.calcChangePercent(prevAmount, amount);
          trend = this.directionFromDelta(momDelta);
        }
      } else if (isIncompleteMonth) {
        trend = 'pending';
      }

      const monthFlat = flat.filter((f) => f.year === year && f.month === month);
      const segments = monthFlat
        .map((f) => ({
          typeFeeId: f.typeFeeId,
          typeFeeName: f.typeFeeName,
          amount: f.amount,
          count: f.receiptCount,
          pct: amount > 0 ? Math.round((f.amount / amount) * 100) : 0,
          color:
            colorByKey.get(`${f.typeFeeId}|${f.typeFeeName}`) ??
            TYPE_FEE_COLORS[Math.abs(f.typeFeeId) % TYPE_FEE_COLORS.length],
        }))
        .filter((s) => s.amount > 0)
        .sort((a, b) => b.amount - a.amount);

      return {
        year,
        month,
        label,
        amount,
        receiptCount,
        amountPct: Math.round((amount / maxAmount) * 100),
        isPeak:
          !!summary.peakMonthLabel &&
          monthKey === summary.peakMonthLabel &&
          amount === summary.peakMonthAmount,
        isLowest:
          !!summary.lowestMonthLabel &&
          monthKey === summary.lowestMonthLabel &&
          amount === summary.lowestMonthAmount,
        isIncompleteMonth,
        prevAmount,
        momDelta,
        momChangePercent,
        trend,
        segments,
      };
    });
  }

  private mapTypeFeeMonthly(
    items: unknown[],
    months: ParkingRevenueMonth[],
    breakdown: ParkingRevenueTypeFee[],
  ): ParkingRevenueTypeFeeSeries[] {
    if (months.length === 0 || breakdown.length === 0) return [];

    const flat = items.map((it) => {
      const r = (it ?? {}) as ParkingRevenueMonthlyTypeFeeSeriesDto & Record<string, unknown>;
      return {
        year: Number(r.year ?? r['Year'] ?? 0),
        month: Number(r.month ?? r['Month'] ?? 0),
        typeFeeId: Number(r.typeFeeId ?? r['TypeFeeId'] ?? 0),
        typeFeeName: String(r.typeFeeName ?? r['TypeFeeName'] ?? 'ไม่ระบุประเภท'),
        amount: Number(r.amount ?? r['Amount'] ?? 0),
      };
    });

    const maxPoint = Math.max(1, ...flat.map((x) => x.amount), ...months.map((m) => m.amount));
    const n = months.length;

    return breakdown.map((tf) => {
      const points = months.map((m) => {
        const hit = flat.find(
          (f) =>
            f.year === m.year &&
            f.month === m.month &&
            f.typeFeeId === tf.typeFeeId &&
            f.typeFeeName === tf.typeFeeName,
        );
        return {
          year: m.year,
          month: m.month,
          label: m.label,
          amount: hit?.amount ?? 0,
        };
      });

      const linePoints = points
        .map((p, i) => {
          const x = n === 1 ? 50 : (i / (n - 1)) * 100;
          const y = 92 - (p.amount / maxPoint) * 78;
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(' ');

      return {
        typeFeeId: tf.typeFeeId,
        typeFeeName: tf.typeFeeName,
        color: tf.color,
        linePoints,
        totalAmount: tf.amount,
        points,
      };
    });
  }

  private buildDonut(types: ParkingRevenueTypeFee[], totalAmount: number): string {
    if (totalAmount <= 0 || types.length === 0) {
      return 'conic-gradient(#e2e8f0 0% 100%)';
    }
    const parts = types.map((t) => `${t.color} ${t.donutStartPct}% ${t.donutEndPct}%`);
    return `conic-gradient(${parts.join(', ')})`;
  }

  private buildTrendInsight(series: ParkingRevenueMonth[]): ParkingRevenueTrendInsight {
    const empty: ParkingRevenueTrendInsight = {
      direction: 'flat',
      label: 'ยังไม่มีแนวโน้ม',
      periodChangePercent: null,
      avgMomPercent: null,
      risingMonths: 0,
      fallingMonths: 0,
      flatMonths: 0,
      firstLabel: '',
      lastLabel: '',
      firstAmount: 0,
      lastAmount: 0,
      linePoints: '',
      lineDots: [],
    };
    if (series.length === 0) return empty;

    const completed = series.filter((m) => !m.isIncompleteMonth);
    const first = completed[0] ?? series[0];
    const last = completed[completed.length - 1] ?? series[0];
    const withMom = completed.filter((m) => m.momDelta !== null && m.trend !== 'pending');
    const risingMonths = withMom.filter((m) => m.trend === 'up').length;
    const fallingMonths = withMom.filter((m) => m.trend === 'down').length;
    const flatMonths = withMom.filter((m) => m.trend === 'flat').length;
    const momPercents = withMom
      .map((m) => m.momChangePercent)
      .filter((v): v is number => v !== null);
    const avgMomPercent =
      momPercents.length > 0
        ? Math.round((momPercents.reduce((a, b) => a + b, 0) / momPercents.length) * 10) / 10
        : null;
    const periodChangePercent =
      completed.length >= 2 ? this.calcChangePercent(first.amount, last.amount) : null;

    let direction: RevenueTrendDirection = 'flat';
    let label = 'แนวโน้มคงที่';

    if (completed.length < 2) {
      direction = series.some((m) => m.isIncompleteMonth) ? 'pending' : 'flat';
      label =
        direction === 'pending'
          ? 'เดือนปัจจุบันยังไม่จบ — ยังไม่สรุปแนวโน้ม'
          : 'ยังไม่มีแนวโน้ม';
    } else if (risingMonths > fallingMonths && risingMonths > flatMonths) {
      direction = 'up';
      label = 'แนวโน้มเพิ่มขึ้น';
    } else if (fallingMonths > risingMonths && fallingMonths > flatMonths) {
      direction = 'down';
      label = 'แนวโน้มลดลง';
    } else if (periodChangePercent !== null) {
      direction = this.directionFromPercent(periodChangePercent);
      label =
        direction === 'up' ? 'แนวโน้มเพิ่มขึ้น' : direction === 'down' ? 'แนวโน้มลดลง' : 'แนวโน้มคงที่';
    }

    if (series.some((m) => m.isIncompleteMonth) && completed.length >= 2) {
      label = `${label} (ไม่นับเดือนปัจจุบัน)`;
    }

    const maxAmount = Math.max(1, ...series.map((m) => m.amount));
    const n = series.length;
    const lineDots = series.map((m, i) => {
      const x = n === 1 ? 50 : (i / (n - 1)) * 100;
      const y = 92 - (m.amount / maxAmount) * 78;
      return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
    });

    return {
      direction,
      label,
      periodChangePercent,
      avgMomPercent,
      risingMonths,
      fallingMonths,
      flatMonths,
      firstLabel: first.label,
      lastLabel: last.label,
      firstAmount: first.amount,
      lastAmount: last.amount,
      linePoints: lineDots.map((p) => `${p.x},${p.y}`).join(' '),
      lineDots,
    };
  }

  private buildKpiCards(summary: ParkingRevenueMarketingResult['summary']): ParkingRevenueKpiCard[] {
    const mom =
      summary.momChangePercent === null
        ? undefined
        : `MoM ${summary.momChangePercent >= 0 ? '+' : ''}${summary.momChangePercent}%`;

    return [
      {
        title: 'รายได้ทั้งหมด',
        value: this.formatMoney(summary.totalAmount),
        icon: 'payments',
        color: 'text-emerald-600',
        hint: mom,
      },
      {
        title: 'จำนวนใบเสร็จ',
        value: summary.receiptCount.toLocaleString(),
        icon: 'receipt_long',
        color: 'text-sky-600',
      },
      {
        title: 'ประเภทค่าธรรมเนียม',
        value: summary.typeFeeCount.toLocaleString(),
        icon: 'category',
        color: 'text-amber-600',
      },
      {
        title: 'ประเภทสูงสุด',
        value: this.formatMoney(summary.topTypeFeeAmount),
        icon: 'emoji_events',
        color: 'text-teal-600',
        hint: summary.topTypeFeeName || undefined,
      },
    ];
  }

  private formatMoney(value: number): string {
    return value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private calcChangePercent(prev: number, curr: number): number {
    if (prev > 0) return Math.round(((curr - prev) * 1000) / prev) / 10;
    if (curr > 0) return 100;
    return 0;
  }

  private directionFromDelta(delta: number): RevenueTrendDirection {
    if (delta > 0) return 'up';
    if (delta < 0) return 'down';
    return 'flat';
  }

  private directionFromPercent(pct: number): RevenueTrendDirection {
    if (pct > 0.5) return 'up';
    if (pct < -0.5) return 'down';
    return 'flat';
  }

  private isIncompleteCalendarMonth(year: number, month: number): boolean {
    const now = new Date();
    return year === now.getFullYear() && month === now.getMonth() + 1;
  }
}
